import React, { useState, useEffect, useCallback, useRef } from "react";
import Avatar from "../../components/ui/Avatar";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Input from "../../components/forms/Input";
import Badge from "../../components/ui/Badge";
import Loader from "../../components/ui/Loader";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import { isAdmin } from "../../utils/permissions";
import { expenseService } from "../../api/expenseService";
import logger from "../../utils/logger";
import { uploadImage, IMAGES_BUCKET } from "../../api/supabaseStorage";
import {
  DollarSign,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Trash2,
  Eye,
  Calculator,
  ChevronDown,
  Search,
  User,
  Users,
  Receipt,
  ImageIcon,
  X,
  Copy,
  Phone,
  AtSign,
  Check,
} from "lucide-react";

const DEBT_STATUS = {
  PENDING: "pending",
  PAYMENT_CLAIMED: "payment_claimed",
  CONFIRMED: "confirmed",
};

const Expenses = () => {
  const { auth } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const employee = auth?.employee;
  const currentUserId = auth?.employee?._id;
  const isPreviewMode =
    searchParams.get("preview") === "true" && isAdmin(employee);

  const [activeTab, setActiveTab] = useState("debts"); // 'debts', 'add', or 'history'
  const [historySubTab, setHistorySubTab] = useState("made"); // 'made' or 'received'
  const [employees, setEmployees] = useState([]);
  const [debts, setDebts] = useState({ owed: [], owedTo: [] });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Receipt upload state
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const [receiptImageModal, setReceiptImageModal] = useState(null); // URL shown in lightbox

  // Payment account state
  const [paymentType, setPaymentType] = useState("phone");
  const [paymentValue, setPaymentValue] = useState("");
  const [paymentValueTouched, setPaymentValueTouched] = useState(false);

  // Tracks which debt's copy button is showing the checkmark
  const [copiedDebtId, setCopiedDebtId] = useState(null);

  // Tracks which debt action button is currently processing { id, action }
  const [processingDebtAction, setProcessingDebtAction] = useState(null);

  // Dropdown state for participant multi-select
  const [isParticipantDropdownOpen, setIsParticipantDropdownOpen] =
    useState(false);
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantDropdownPos, setParticipantDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const participantDropdownRef = useRef(null); // wrapper — used for click-outside
  const participantTriggerRef = useRef(null); // button — used to measure position
  const participantMenuRef = useRef(null); // portal content — used for click-outside
  const receiptInputRef = useRef(null);

  // Dropdown state for Paid By selection
  const [isPaidByDropdownOpen, setIsPaidByDropdownOpen] = useState(false);
  const [paidBySearchQuery, setPaidBySearchQuery] = useState("");
  const paidByDropdownRef = useRef(null);

  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    description: "",
    totalAmount: "",
    paidBy: currentUserId || "",
    participants: [],
  });

  const [participantInputs, setParticipantInputs] = useState([]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const response = await expenseService.getEmployees();
      setEmployees(response.data?.data || response.data || []);
    } catch (error) {
      logger.error("Failed to fetch employees:", error);
      showToast("Failed to load employees", "error");
    } finally {
      setLoadingEmployees(false);
    }
  }, [showToast]);

  const fetchDebts = useCallback(
    async (isRealTimeUpdate = false) => {
      try {
        if (isRealTimeUpdate) {
          setIsRefreshing(true);
        } else {
          setLoading(true);
        }
        const response = await expenseService.getUserDebts();
        const debtsData = response.data?.data || response.data || {};
        setDebts({
          owed: debtsData.owed || [],
          owedTo: debtsData.owedTo || [],
        });
      } catch (error) {
        logger.error("Failed to fetch debts:", error);
        if (!isRealTimeUpdate) {
          showToast("Failed to load debts", "error");
        }
      } finally {
        if (isRealTimeUpdate) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [showToast],
  );

  const fetchPaymentHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const response = await expenseService.getPaymentHistory();
      const historyData = response.data?.data || response.data || [];
      setPaymentHistory(historyData);
    } catch (error) {
      logger.error("Failed to fetch payment history:", error);
      showToast("Failed to load payment history", "error");
    } finally {
      setLoadingHistory(false);
    }
  }, [showToast]);

  // Fetch data on mount
  useEffect(() => {
    fetchEmployees();
    fetchDebts();
  }, [fetchEmployees, fetchDebts]);

  // Auto-fill payment value when type changes or employee profile loads
  useEffect(() => {
    if (paymentType === "phone") {
      setPaymentValue(employee?.phone || "");
    } else {
      setPaymentValue("");
    }
  }, [paymentType, employee?.phone]);

  // Fetch payment history when History tab is active
  useEffect(() => {
    if (activeTab === "history" && currentUserId) {
      fetchPaymentHistory();
    }
  }, [activeTab, currentUserId, fetchPaymentHistory]);

  // Listen for real-time expense updates via Firebase
  useEffect(() => {
    if (!currentUserId) return;

    // Handle expense added event
    const handleExpenseAdded = (event) => {
      const { debtorId, creditorId } = event.detail;
      // Only update if this affects the current user
      if (debtorId === currentUserId || creditorId === currentUserId) {
        fetchDebts(true);
      }
    };

    // Handle payment claimed event
    const handlePaymentClaimed = (event) => {
      const { creditorId } = event.detail;
      // Only update if current user is the creditor
      if (creditorId === currentUserId) {
        fetchDebts(true);
      }
    };

    // Handle payment confirmed event
    const handlePaymentConfirmed = (event) => {
      const { debtorId, creditorId } = event.detail;
      // Update if this affects the current user
      if (debtorId === currentUserId || creditorId === currentUserId) {
        fetchDebts(true);
      }
    };

    // Handle payment rejected event
    const handlePaymentRejected = (event) => {
      const { debtorId, creditorId } = event.detail;
      // Update if this affects the current user
      if (debtorId === currentUserId || creditorId === currentUserId) {
        fetchDebts(true);
      }
    };

    // Register event listeners
    window.addEventListener("expense-added", handleExpenseAdded);
    window.addEventListener("payment-claimed", handlePaymentClaimed);
    window.addEventListener("payment-confirmed", handlePaymentConfirmed);
    window.addEventListener("payment-rejected", handlePaymentRejected);

    // Cleanup
    return () => {
      window.removeEventListener("expense-added", handleExpenseAdded);
      window.removeEventListener("payment-claimed", handlePaymentClaimed);
      window.removeEventListener("payment-confirmed", handlePaymentConfirmed);
      window.removeEventListener("payment-rejected", handlePaymentRejected);
    };
  }, [currentUserId, fetchDebts]);

  // Add a single employee to participants (prevents duplicates)
  const handleSelectParticipant = (employeeId) => {
    if (participantInputs.some((p) => p.employeeId === employeeId)) return;
    setParticipantInputs([...participantInputs, { employeeId, share: "" }]);
  };

  // Add all employees not already selected
  const handleSelectAll = () => {
    const currentIds = new Set(
      participantInputs.map((p) => p.employeeId).filter(Boolean),
    );
    const toAdd = employees
      .filter((emp) => !currentIds.has(emp._id))
      .map((emp) => ({ employeeId: emp._id, share: "" }));
    setParticipantInputs([
      ...participantInputs.filter((p) => p.employeeId),
      ...toAdd,
    ]);
  };

  const handleRemoveParticipant = (index) => {
    setParticipantInputs(participantInputs.filter((_, i) => i !== index));
  };

  const handleParticipantShareChange = (index, value) => {
    const updated = [...participantInputs];
    updated[index] = { ...updated[index], share: value };
    setParticipantInputs(updated);
  };

  const handleSplitBill = () => {
    // Validate total amount
    const totalAmount = parseFloat(expenseForm.totalAmount);
    if (!totalAmount || totalAmount <= 0) {
      showToast("Please enter a valid total amount first", "error");
      return;
    }

    // Get participants with selected employees
    const participantsWithEmployee = participantInputs.filter(
      (p) => p.employeeId && p.employeeId.trim() !== "",
    );

    if (participantsWithEmployee.length === 0) {
      showToast("Please select at least one participant first", "error");
      return;
    }

    // Calculate equal share
    const equalShare = (totalAmount / participantsWithEmployee.length).toFixed(
      2,
    );

    // Update shares for all participants
    const updated = participantInputs.map((participant) => {
      // Only update if participant has an employee selected
      if (participant.employeeId && participant.employeeId.trim() !== "") {
        return { ...participant, share: equalShare };
      }
      return participant;
    });

    setParticipantInputs(updated);
    showToast(
      `Bill split equally: ${equalShare} EGP per person (${participantsWithEmployee.length} participants)`,
      "success",
    );
  };

  // Receipt file selection — validates type and size on the frontend
  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate MIME type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showToast("Only JPG, PNG, and WEBP images are allowed", "error");
      return;
    }
    // Validate size (max 5 MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast("Receipt image must be smaller than 5 MB", "error");
      return;
    }

    if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
    setReceiptFile(file);
    setReceiptPreviewUrl(URL.createObjectURL(file));
    // Reset so the same file can be re-selected if removed
    e.target.value = "";
  };

  const handleRemoveReceipt = () => {
    if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
    setReceiptFile(null);
    setReceiptPreviewUrl(null);
    if (receiptInputRef.current) receiptInputRef.current.value = "";
  };

  const validateExpenseForm = () => {
    if (!expenseForm.description.trim()) {
      showToast("Please enter expense description", "error");
      return false;
    }

    const totalAmount = parseFloat(expenseForm.totalAmount);
    if (!totalAmount || totalAmount <= 0) {
      showToast("Please enter a valid total amount", "error");
      return false;
    }

    if (!expenseForm.paidBy) {
      showToast("Please select who paid", "error");
      return false;
    }

    // Validate participants
    const validParticipants = participantInputs.filter(
      (p) => p.employeeId && p.share,
    );

    if (validParticipants.length === 0) {
      showToast("Please add at least one participant", "error");
      return false;
    }

    // Validate shares
    const totalShares = validParticipants.reduce(
      (sum, p) => sum + parseFloat(p.share || 0),
      0,
    );

    if (Math.abs(totalShares - totalAmount) > 0.01) {
      showToast(
        `Total shares (${totalShares.toFixed(2)}) must equal total amount (${totalAmount.toFixed(2)})`,
        "error",
      );
      return false;
    }

    // Validate payment account
    if (!paymentValue.trim()) {
      showToast("Please enter a payment account", "error");
      return false;
    }
    if (paymentType === "phone") {
      const phoneRegex = /^01[0125]\d{8}$/;
      if (!phoneRegex.test(paymentValue.trim())) {
        showToast(
          "Please enter a valid Egyptian phone number (e.g. 01xxxxxxxxx)",
          "error",
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmitExpense = async () => {
    if (!validateExpenseForm()) return;

    try {
      setSubmitting(true);

      // Upload receipt image to Supabase (if provided)
      let receiptUrl = null;
      if (receiptFile) {
        const ext = receiptFile.name.split(".").pop().toLowerCase();
        const path = `expense-receipts/${currentUserId}_${Date.now()}.${ext}`;
        const { publicUrl, error: uploadError } = await uploadImage(
          IMAGES_BUCKET,
          path,
          receiptFile,
        );
        if (uploadError) {
          showToast(uploadError.message || "Receipt upload failed", "error");
          setSubmitting(false);
          return;
        }
        receiptUrl = publicUrl;
      }

      const participants = participantInputs
        .filter((p) => p.employeeId && p.share)
        .map((p) => ({
          employeeId: p.employeeId,
          share: parseFloat(p.share),
        }));

      await expenseService.createExpense({
        description: expenseForm.description.trim(),
        totalAmount: parseFloat(expenseForm.totalAmount),
        paidBy: expenseForm.paidBy,
        participants,
        receiptUrl,
        paymentType,
        paymentValue: paymentValue.trim(),
      });

      showToast("Expense created successfully", "success");

      // Reset form
      setExpenseForm({
        description: "",
        totalAmount: "",
        paidBy: currentUserId || "",
        participants: [],
      });
      setParticipantInputs([]);
      handleRemoveReceipt();
      setPaymentType("phone");
      setPaymentValue(employee?.phone || "");
      setPaymentValueTouched(false);

      // Refresh debts and switch to debts tab
      // Real-time update will also trigger, but this ensures immediate UI update
      await fetchDebts(true);
      setActiveTab("debts");
    } catch (error) {
      logger.error("Failed to create expense:", error);
      showToast(
        error.response?.data?.message || "Failed to create expense",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaimPayment = async (debtId) => {
    try {
      setProcessingDebtAction({ id: debtId, action: "claim" });
      await expenseService.claimPayment(debtId);
      showToast("Payment claimed successfully", "success");
      await fetchDebts(true);
    } catch (error) {
      logger.error("Failed to claim payment:", error);
      showToast(
        error.response?.data?.message || "Failed to claim payment",
        "error",
      );
    } finally {
      setProcessingDebtAction(null);
    }
  };

  const handleConfirmPayment = async (debtId) => {
    try {
      setProcessingDebtAction({ id: debtId, action: "confirm" });
      await expenseService.confirmPayment(debtId);
      showToast("Payment confirmed successfully", "success");
      await fetchDebts(true);
      if (activeTab === "history") {
        await fetchPaymentHistory();
      }
    } catch (error) {
      logger.error("Failed to confirm payment:", error);
      showToast(
        error.response?.data?.message || "Failed to confirm payment",
        "error",
      );
    } finally {
      setProcessingDebtAction(null);
    }
  };

  const handleRejectPayment = async (debtId) => {
    try {
      setProcessingDebtAction({ id: debtId, action: "reject" });
      await expenseService.rejectPayment(debtId);
      showToast("Payment rejected", "success");
      await fetchDebts(true);
    } catch (error) {
      logger.error("Failed to reject payment:", error);
      showToast(
        error.response?.data?.message || "Failed to reject payment",
        "error",
      );
    } finally {
      setProcessingDebtAction(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case DEBT_STATUS.PENDING:
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      case DEBT_STATUS.PAYMENT_CLAIMED:
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Payment Claimed
          </Badge>
        );
      case DEBT_STATUS.CONFIRMED:
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Confirmed
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getEmployeeName = (employee) => {
    return employee?.fullNameEn || employee?.nickname || "Unknown";
  };

  // IDs of already-selected participants (for dedup)
  const selectedParticipantIds = new Set(
    participantInputs.map((p) => p.employeeId).filter(Boolean),
  );

  // Filter participants by search query AND exclude already-selected employees
  const filteredParticipants = employees.filter((emp) => {
    if (selectedParticipantIds.has(emp._id)) return false;
    const query = participantSearch.toLowerCase();
    return (
      getEmployeeName(emp).toLowerCase().includes(query) ||
      emp.employeeCode?.toLowerCase().includes(query)
    );
  });

  // Filter Paid By employees by search query
  const filteredPaidByEmployees = employees.filter((emp) => {
    const query = paidBySearchQuery.toLowerCase();
    return (
      getEmployeeName(emp).toLowerCase().includes(query) ||
      emp.employeeCode?.toLowerCase().includes(query)
    );
  });

  const totalOwed = debts.owed.reduce((sum, d) => sum + d.amount, 0);
  const totalOwedTo = debts.owedTo.reduce((sum, d) => sum + d.amount, 0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Handle participant multi-select dropdown (portal-aware: check both trigger wrapper and menu)
      if (isParticipantDropdownOpen) {
        const inTrigger = participantDropdownRef.current?.contains(e.target);
        const inMenu = participantMenuRef.current?.contains(e.target);
        if (!inTrigger && !inMenu) {
          setIsParticipantDropdownOpen(false);
          setParticipantSearch("");
        }
      }
      // Handle Paid By dropdown
      if (isPaidByDropdownOpen && paidByDropdownRef.current) {
        if (!paidByDropdownRef.current.contains(e.target)) {
          setIsPaidByDropdownOpen(false);
          setPaidBySearchQuery("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isParticipantDropdownOpen, isPaidByDropdownOpen]);

  // Loading Skeleton Component
  const DebtSkeleton = () => (
    <div className="bg-white dark:bg-primary-card p-4 rounded-lg border border-gray-200 dark:border-dark-100 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-3">
            <div className="h-5 bg-gray-200 dark:bg-dark-200 rounded w-48"></div>
            <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded-full w-20"></div>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-dark-200 rounded w-32"></div>
          <div className="h-4 bg-gray-200 dark:bg-dark-200 rounded w-24"></div>
        </div>
        <div className="text-right space-y-2">
          <div className="h-7 bg-gray-200 dark:bg-dark-200 rounded w-24 ml-auto"></div>
          <div className="h-9 bg-gray-200 dark:bg-dark-200 rounded w-20 ml-auto"></div>
        </div>
      </div>
    </div>
  );

  const SummaryCardSkeleton = () => (
    <div className="bg-white dark:bg-primary-card p-6 rounded-lg border border-gray-200 dark:border-dark-100 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-3 flex-1">
          <div className="h-4 bg-gray-200 dark:bg-dark-200 rounded w-20"></div>
          <div className="h-8 bg-gray-200 dark:bg-dark-200 rounded w-32"></div>
          <div className="h-3 bg-gray-200 dark:bg-dark-200 rounded w-24"></div>
        </div>
        <div className="w-12 h-12 bg-gray-200 dark:bg-dark-200 rounded-full"></div>
      </div>
    </div>
  );

  const SectionHeaderSkeleton = () => (
    <div className="flex items-center gap-2 mb-4 animate-pulse">
      <div className="w-5 h-5 bg-gray-200 dark:bg-dark-200 rounded-full"></div>
      <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-48"></div>
    </div>
  );

  return (
    <>
      <PageWrapper>
        <div className="space-y-6">
          {/* Preview Mode Banner */}
          {isPreviewMode && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center gap-3"
            >
              <Eye className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Preview Mode - Read Only
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                  You can view the page content but cannot perform any actions.
                  All buttons and forms are disabled.
                </p>
              </div>
            </motion.div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-light-100 dark:text-light-100">
                Expense Sharing
              </h1>
              <p className="text-sm text-light-500 dark:text-light-500 mt-1">
                Track and settle shared expenses with your team
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 dark:border-dark-100">
            <button
              onClick={() => !isPreviewMode && setActiveTab("debts")}
              disabled={isPreviewMode}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === "debts"
                  ? "text-primary-500 border-b-2 border-primary-500"
                  : "text-light-500 hover:text-light-100"
              } ${isPreviewMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              My Debts
            </button>
            <button
              onClick={() => !isPreviewMode && setActiveTab("add")}
              disabled={isPreviewMode}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === "add"
                  ? "text-primary-500 border-b-2 border-primary-500"
                  : "text-light-500 hover:text-light-100"
              } ${isPreviewMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              Add Expense
            </button>
            <button
              onClick={() => !isPreviewMode && setActiveTab("history")}
              disabled={isPreviewMode}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === "history"
                  ? "text-primary-500 border-b-2 border-primary-500"
                  : "text-light-500 hover:text-light-100"
              } ${isPreviewMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              Payment History
            </button>
          </div>

          {/* Debts Tab */}
          {activeTab === "debts" && (
            <div className="space-y-6">
              {/* Real-time refresh indicator */}
              {isRefreshing && (
                <div className="flex items-center justify-center py-2 text-sm text-primary-500">
                  <Loader className="w-4 h-4 mr-2" />
                  Updating...
                </div>
              )}
              {loading ? (
                <>
                  {/* Summary Cards Skeleton */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SummaryCardSkeleton />
                    <SummaryCardSkeleton />
                  </div>

                  {/* Debts Owed Skeleton */}
                  <div>
                    <SectionHeaderSkeleton />
                    <div className="space-y-3">
                      <DebtSkeleton />
                      <DebtSkeleton />
                      <DebtSkeleton />
                    </div>
                  </div>

                  {/* Debts Owed To Skeleton */}
                  <div>
                    <SectionHeaderSkeleton />
                    <div className="space-y-3">
                      <DebtSkeleton />
                      <DebtSkeleton />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white dark:bg-primary-card p-6 rounded-lg border border-gray-200 dark:border-dark-100"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-light-500 dark:text-light-500">
                            You Owe
                          </p>
                          <p className="text-2xl font-bold text-red-500 mt-1">
                            {totalOwed.toFixed(2)} EGP
                          </p>
                          <p className="text-xs text-light-500 dark:text-light-500 mt-1">
                            {debts.owed.length} debt
                            {debts.owed.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                        <ArrowDownCircle className="w-12 h-12 text-red-500" />
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      className="bg-white dark:bg-primary-card p-6 rounded-lg border border-gray-200 dark:border-dark-100"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-light-500 dark:text-light-500">
                            Owed To You
                          </p>
                          <p className="text-2xl font-bold text-green-500 mt-1">
                            {totalOwedTo.toFixed(2)} EGP
                          </p>
                          <p className="text-xs text-light-500 dark:text-light-500 mt-1">
                            {debts.owedTo.length} debt
                            {debts.owedTo.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                        <ArrowUpCircle className="w-12 h-12 text-green-500" />
                      </div>
                    </motion.div>
                  </div>

                  {/* Debts Owed */}
                  <div>
                    <h2 className="text-lg font-semibold text-light-100 dark:text-light-100 mb-4 flex items-center gap-2">
                      <ArrowDownCircle className="w-5 h-5 text-red-500" />
                      Money You Owe ({debts.owed.length})
                    </h2>
                    {debts.owed.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white dark:bg-primary-card p-8 rounded-lg border border-gray-200 dark:border-dark-100 text-center"
                      >
                        <p className="text-light-500 dark:text-light-500">
                          No debts to pay. Great job!
                        </p>
                      </motion.div>
                    ) : (
                      <div className="space-y-3">
                        {debts.owed.map((debt, index) => (
                          <motion.div
                            key={debt._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="bg-white dark:bg-primary-card p-4 rounded-lg border border-gray-200 dark:border-dark-100"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-semibold text-light-100 dark:text-light-100">
                                    {debt.expenseId?.description ||
                                      "Unknown Expense"}
                                  </p>
                                  {getStatusBadge(debt.status)}
                                  {debt.originalDebtIds &&
                                    debt.originalDebtIds.length > 0 && (
                                      <Badge variant="info" size="sm">
                                        <Calculator className="w-3 h-3 mr-1" />
                                        Net Balance
                                      </Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5 text-sm text-light-500 dark:text-light-500">
                                  <User className="w-3.5 h-3.5 shrink-0" />
                                  <span>
                                    To: {getEmployeeName(debt.creditorId)}
                                  </span>
                                </div>
                                {debt.expenseId?.paymentValue && (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    {debt.expenseId.paymentType ===
                                    "instapay" ? (
                                      <AtSign className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                    ) : (
                                      <Phone className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                    )}
                                    <span className="text-xs text-gray-500 dark:text-light-500 shrink-0">
                                      {debt.expenseId.paymentType === "instapay"
                                        ? "InstaPay:"
                                        : "Phone:"}
                                    </span>
                                    <span className="text-sm font-semibold text-light-100 dark:text-light-200">
                                      {debt.expenseId.paymentValue}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          debt.expenseId.paymentValue,
                                        );
                                        setCopiedDebtId(debt._id);
                                        setTimeout(
                                          () => setCopiedDebtId(null),
                                          2000,
                                        );
                                      }}
                                      className={`p-1 transition-colors rounded ${
                                        copiedDebtId === debt._id
                                          ? "text-green-500"
                                          : "text-gray-400 hover:text-primary-accent"
                                      }`}
                                      title="Copy to clipboard"
                                    >
                                      {copiedDebtId === debt._id ? (
                                        <Check className="w-3.5 h-3.5" />
                                      ) : (
                                        <Copy className="w-3.5 h-3.5" />
                                      )}
                                    </button>
                                  </div>
                                )}
                                {debt.expenseId?.receiptUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setReceiptImageModal(
                                        debt.expenseId.receiptUrl,
                                      )
                                    }
                                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-dark-200 bg-gray-50 dark:bg-dark-100 text-gray-600 dark:text-light-400 hover:border-primary-accent hover:text-primary-accent dark:hover:text-orange-400 transition-colors"
                                  >
                                    <Receipt className="w-3.5 h-3.5" />
                                    View Receipt
                                  </button>
                                )}
                                {debt.originalDebtIds &&
                                  debt.originalDebtIds.length > 0 && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                      ✓ Simplified from{" "}
                                      {debt.originalDebtIds.length} original
                                      debt
                                      {debt.originalDebtIds.length !== 1
                                        ? "s"
                                        : ""}
                                      {debt.originalDebtIds.length >= 2 &&
                                        " (offset or chain settlement)"}
                                    </p>
                                  )}
                                <p className="text-sm text-light-500 dark:text-light-500 mt-1">
                                  Date: {formatDate(debt.createdAt)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-red-500">
                                  {debt.amount.toFixed(2)} EGP
                                </p>
                                {debt.status === DEBT_STATUS.PENDING && (
                                  <Button
                                    onClick={() => handleClaimPayment(debt._id)}
                                    className="mt-2"
                                    size="sm"
                                    disabled={
                                      isPreviewMode ||
                                      processingDebtAction?.id === debt._id
                                    }
                                  >
                                    {processingDebtAction?.id === debt._id &&
                                    processingDebtAction?.action === "claim" ? (
                                      <>
                                        <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                        Processing...
                                      </>
                                    ) : (
                                      "I Paid"
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Debts Owed To */}
                  <div>
                    <h2 className="text-lg font-semibold text-light-100 dark:text-light-100 mb-4 flex items-center gap-2">
                      <ArrowUpCircle className="w-5 h-5 text-green-500" />
                      Money Owed To You ({debts.owedTo.length})
                    </h2>
                    {debts.owedTo.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white dark:bg-primary-card p-8 rounded-lg border border-gray-200 dark:border-dark-100 text-center"
                      >
                        <p className="text-light-500 dark:text-light-500">
                          No one owes you money.
                        </p>
                      </motion.div>
                    ) : (
                      <div className="space-y-3">
                        {debts.owedTo.map((debt, index) => (
                          <motion.div
                            key={debt._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="bg-white dark:bg-primary-card p-4 rounded-lg border border-gray-200 dark:border-dark-100"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-semibold text-light-100 dark:text-light-100">
                                    {getEmployeeName(debt.debtorId)}
                                  </p>
                                  {getStatusBadge(debt.status)}
                                  {debt.originalDebtIds &&
                                    debt.originalDebtIds.length > 0 && (
                                      <Badge variant="info" size="sm">
                                        <Calculator className="w-3 h-3 mr-1" />
                                        Net Balance
                                      </Badge>
                                    )}
                                </div>
                                <p className="text-sm text-light-500 dark:text-light-500">
                                  {debt.expenseId?.description ||
                                    "Unknown Expense"}
                                </p>
                                {debt.expenseId?.paymentValue && (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    {debt.expenseId.paymentType ===
                                    "instapay" ? (
                                      <AtSign className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                    ) : (
                                      <Phone className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                    )}
                                    <span className="text-xs text-gray-500 dark:text-light-500 shrink-0">
                                      {debt.expenseId.paymentType === "instapay"
                                        ? "InstaPay:"
                                        : "Phone:"}
                                    </span>
                                    <span className="text-sm font-semibold text-light-100 dark:text-light-200">
                                      {debt.expenseId.paymentValue}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          debt.expenseId.paymentValue,
                                        );
                                        setCopiedDebtId(debt._id);
                                        setTimeout(
                                          () => setCopiedDebtId(null),
                                          2000,
                                        );
                                      }}
                                      className={`p-1 transition-colors rounded ${
                                        copiedDebtId === debt._id
                                          ? "text-green-500"
                                          : "text-gray-400 hover:text-primary-accent"
                                      }`}
                                      title="Copy to clipboard"
                                    >
                                      {copiedDebtId === debt._id ? (
                                        <Check className="w-3.5 h-3.5" />
                                      ) : (
                                        <Copy className="w-3.5 h-3.5" />
                                      )}
                                    </button>
                                  </div>
                                )}
                                {debt.expenseId?.receiptUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setReceiptImageModal(
                                        debt.expenseId.receiptUrl,
                                      )
                                    }
                                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-dark-200 bg-gray-50 dark:bg-dark-100 text-gray-600 dark:text-light-400 hover:border-primary-accent hover:text-primary-accent dark:hover:text-orange-400 transition-colors"
                                  >
                                    <Receipt className="w-3.5 h-3.5" />
                                    View Receipt
                                  </button>
                                )}
                                {debt.originalDebtIds &&
                                  debt.originalDebtIds.length > 0 && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                      ✓ Simplified from{" "}
                                      {debt.originalDebtIds.length} original
                                      debt
                                      {debt.originalDebtIds.length !== 1
                                        ? "s"
                                        : ""}
                                      {debt.originalDebtIds.length >= 2 &&
                                        " (offset or chain settlement)"}
                                    </p>
                                  )}
                                <p className="text-sm text-light-500 dark:text-light-500 mt-1">
                                  Date: {formatDate(debt.createdAt)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-green-500">
                                  {debt.amount.toFixed(2)} EGP
                                </p>
                                {debt.status ===
                                  DEBT_STATUS.PAYMENT_CLAIMED && (
                                  <div className="flex gap-2 mt-2">
                                    <Button
                                      onClick={() =>
                                        handleConfirmPayment(debt._id)
                                      }
                                      className="bg-green-500 hover:bg-green-600"
                                      size="sm"
                                      disabled={
                                        isPreviewMode ||
                                        processingDebtAction?.id === debt._id
                                      }
                                    >
                                      {processingDebtAction?.id === debt._id &&
                                      processingDebtAction?.action ===
                                        "confirm" ? (
                                        <>
                                          <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                          Processing...
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle2 className="w-4 h-4" />
                                          Accept
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      onClick={() =>
                                        handleRejectPayment(debt._id)
                                      }
                                      variant="danger"
                                      size="sm"
                                      disabled={
                                        isPreviewMode ||
                                        processingDebtAction?.id === debt._id
                                      }
                                    >
                                      {processingDebtAction?.id === debt._id &&
                                      processingDebtAction?.action ===
                                        "reject" ? (
                                        <>
                                          <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                          Processing...
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="w-4 h-4" />
                                          Reject
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Add Expense Tab */}
          {activeTab === "add" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden"
            >
              {/* Gradient accent header */}
              <div className="bg-gradient-to-r from-primary-accent to-orange-400 px-6 py-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white leading-tight">
                    Create New Expense
                  </h2>
                  <p className="text-xs text-white/75 mt-0.5">
                    Record a shared expense and split it with your team
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Description + Amount — side by side on md+ */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Description */}
                  <Input
                    label="Expense Description"
                    value={expenseForm.description}
                    onChange={(e) =>
                      setExpenseForm({
                        ...expenseForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="e.g., Team lunch, Office supplies"
                    required
                    disabled={isPreviewMode}
                  />

                  {/* Total Amount */}
                  <Input
                    label="Total Amount (EGP)"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={expenseForm.totalAmount}
                    onChange={(e) =>
                      setExpenseForm({
                        ...expenseForm,
                        totalAmount: e.target.value,
                      })
                    }
                    placeholder="0.00"
                    required
                    disabled={isPreviewMode}
                  />
                </div>

                {/* Paid By */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-light-200 mb-1.5 block">
                    Paid By <span className="text-red-500">*</span>
                  </label>
                  {loadingEmployees ? (
                    <div className="h-11 bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
                  ) : (
                    <div className="relative" ref={paidByDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          if (!isPreviewMode) {
                            setIsPaidByDropdownOpen(!isPaidByDropdownOpen);
                            setPaidBySearchQuery("");
                          }
                        }}
                        disabled={isPreviewMode}
                        className="w-full flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-left text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all cursor-pointer hover:border-primary-accent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {expenseForm.paidBy ? (
                          (() => {
                            const emp = employees.find((e) => e._id === expenseForm.paidBy);
                            return <Avatar src={emp?.photo} name={emp ? getEmployeeName(emp) : "?"} className="!w-7 !h-7 !text-[10.5px]" />;
                          })()
                        ) : (
                          <User className="w-4 h-4 text-gray-400 shrink-0" />
                        )}
                        <span className="flex-1 truncate flex items-center gap-1.5">
                          {expenseForm.paidBy
                            ? (() => {
                                const emp = employees.find(
                                  (e) => e._id === expenseForm.paidBy,
                                );
                                return emp ? (
                                  <>
                                    {getEmployeeName(emp)}
                                    {emp._id === currentUserId && (
                                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-accent/15 text-primary-accent dark:text-orange-400 shrink-0">
                                        You
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  "Select who paid"
                                );
                              })()
                            : "Select who paid"}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                            isPaidByDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isPaidByDropdownOpen && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg shadow-lg overflow-hidden">
                          <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                value={paidBySearchQuery}
                                onChange={(e) =>
                                  setPaidBySearchQuery(e.target.value)
                                }
                                placeholder="Search employees..."
                                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filteredPaidByEmployees.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-500 dark:text-light-500 text-center">
                                No employees found
                              </div>
                            ) : (
                              filteredPaidByEmployees.map((emp) => {
                                const isSelected =
                                  emp._id === expenseForm.paidBy;
                                return (
                                  <button
                                    key={emp._id}
                                    type="button"
                                    onClick={() => {
                                      setExpenseForm({
                                        ...expenseForm,
                                        paidBy: emp._id,
                                      });
                                      setIsPaidByDropdownOpen(false);
                                      setPaidBySearchQuery("");
                                    }}
                                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 transition-colors ${
                                      isSelected
                                        ? "bg-primary-accent/10 text-primary-accent font-medium"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                                        {getEmployeeName(emp)}
                                      </span>
                                      {emp._id === currentUserId && (
                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-accent/15 text-primary-accent dark:text-orange-400">
                                          You
                                        </span>
                                      )}
                                    </div>
                                    {emp.employeeCode && (
                                      <div className="text-xs text-gray-500 dark:text-light-500">
                                        {emp.employeeCode}
                                      </div>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Account */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-light-200 mb-1.5 block">
                    Payment Account <span className="text-red-500">*</span>
                  </label>
                  {(() => {
                    const phoneRegex = /^01[0125]\d{8}$/;
                    const phoneError =
                      paymentType === "phone" &&
                      paymentValueTouched &&
                      !phoneRegex.test(paymentValue.trim())
                        ? paymentValue.trim() === ""
                          ? "Phone number is required"
                          : "Invalid phone number. Must be 11 digits starting with 01"
                        : null;
                    return (
                      <>
                        <div className="flex gap-2">
                          <select
                            value={paymentType}
                            onChange={(e) => {
                              setPaymentType(e.target.value);
                              setPaymentValueTouched(false);
                            }}
                            disabled={isPreviewMode}
                            className="shrink-0 px-3 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="phone">Phone Number</option>
                            <option value="instapay">InstaPay Username</option>
                          </select>
                          <input
                            type={paymentType === "phone" ? "tel" : "text"}
                            value={paymentValue}
                            onChange={(e) => setPaymentValue(e.target.value)}
                            onBlur={() => setPaymentValueTouched(true)}
                            placeholder={
                              paymentType === "phone"
                                ? "01xxxxxxxxx"
                                : "@instapay_username"
                            }
                            disabled={isPreviewMode}
                            className={`flex-1 px-4 py-2.5 bg-white dark:bg-dark-100 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400 dark:placeholder:text-light-500 border ${
                              phoneError
                                ? "border-red-500 focus:ring-red-400"
                                : "border-gray-300 dark:border-dark-200 focus:ring-primary-accent"
                            }`}
                          />
                        </div>
                        {phoneError ? (
                          <p className="mt-1 text-xs text-red-500">
                            {phoneError}
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500 dark:text-light-500">
                            {paymentType === "phone"
                              ? "Phone number where participants should send money"
                              : "InstaPay username where participants should send money"}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Participants */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-light-200 mb-2 block">
                    Participants & Shares
                  </label>
                  {loadingEmployees ? (
                    <div className="space-y-2">
                      <div className="h-11 bg-gray-200 dark:bg-dark-100 rounded-lg animate-pulse" />
                      <div className="h-11 bg-gray-200 dark:bg-dark-100 rounded-lg animate-pulse" />
                    </div>
                  ) : (
                    <>
                      {/* Single multi-select dropdown trigger */}
                      <div className="relative" ref={participantDropdownRef}>
                        <button
                          ref={participantTriggerRef}
                          type="button"
                          onClick={() => {
                            if (!isPreviewMode) {
                              if (
                                !isParticipantDropdownOpen &&
                                participantTriggerRef.current
                              ) {
                                const rect =
                                  participantTriggerRef.current.getBoundingClientRect();
                                setParticipantDropdownPos({
                                  top: rect.bottom + 4,
                                  left: rect.left,
                                  width: rect.width,
                                });
                              }
                              setIsParticipantDropdownOpen(
                                !isParticipantDropdownOpen,
                              );
                              setParticipantSearch("");
                            }
                          }}
                          disabled={isPreviewMode}
                          className="w-full flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-left focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all cursor-pointer hover:border-primary-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Users className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="flex-1 text-sm text-gray-500 dark:text-light-500 truncate">
                            {selectedParticipantIds.size > 0
                              ? `${selectedParticipantIds.size} participant${
                                  selectedParticipantIds.size !== 1 ? "s" : ""
                                } selected`
                              : "Select participants..."}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                              isParticipantDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isParticipantDropdownOpen &&
                          createPortal(
                            <div
                              ref={participantMenuRef}
                              style={{
                                position: "fixed",
                                top: participantDropdownPos.top,
                                left: participantDropdownPos.left,
                                width: participantDropdownPos.width,
                                zIndex: 9999,
                              }}
                              className="bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg shadow-xl overflow-hidden"
                            >
                              {/* Search input */}
                              <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={participantSearch}
                                    onChange={(e) =>
                                      setParticipantSearch(e.target.value)
                                    }
                                    placeholder="Search employees..."
                                    autoFocus
                                    className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              {/* Select All / Deselect All row */}
                              {!participantSearch && (
                                <div className="px-3 py-2 border-b border-gray-100 dark:border-dark-200">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (
                                        selectedParticipantIds.size ===
                                        employees.length
                                      ) {
                                        setParticipantInputs([]);
                                      } else {
                                        handleSelectAll();
                                      }
                                    }}
                                    className="w-full text-left px-2 py-1.5 text-sm font-semibold text-primary-accent hover:bg-primary-accent/10 rounded-md transition-colors flex items-center gap-2"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                    {selectedParticipantIds.size ===
                                    employees.length
                                      ? "Deselect All"
                                      : "Select All"}
                                  </button>
                                </div>
                              )}
                              {/* Unselected employee list */}
                              <div className="max-h-52 overflow-y-auto">
                                {filteredParticipants.length === 0 ? (
                                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-light-500 text-center">
                                    {participantSearch
                                      ? "No employees found"
                                      : "All employees already selected"}
                                  </div>
                                ) : (
                                  filteredParticipants.map((emp) => (
                                    <button
                                      key={emp._id}
                                      type="button"
                                      onClick={() =>
                                        handleSelectParticipant(emp._id)
                                      }
                                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 transition-colors"
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                                          {getEmployeeName(emp)}
                                        </span>
                                        {emp._id === currentUserId && (
                                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-accent/15 text-primary-accent dark:text-orange-400">
                                            You
                                          </span>
                                        )}
                                      </div>
                                      {emp.employeeCode && (
                                        <div className="text-xs text-gray-500 dark:text-light-500">
                                          {emp.employeeCode}
                                        </div>
                                      )}
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>,
                            document.body,
                          )}
                      </div>

                      {/* Chips + share inputs for selected participants */}
                      {participantInputs.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {participantInputs.map((participant, index) => {
                            const emp = employees.find(
                              (e) => e._id === participant.employeeId,
                            );
                            return (
                              <div
                                key={index}
                                className="flex items-center gap-3 p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-100 shadow-sm"
                              >
                                <Avatar src={emp?.photo} name={getEmployeeName(emp)} size="sm" />
                                {/* Name */}
                                <span className="flex-1 text-sm font-medium text-gray-800 dark:text-light-100 truncate flex items-center gap-1.5">
                                  {getEmployeeName(emp)}
                                  {emp?._id === currentUserId && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-accent/15 text-primary-accent dark:text-orange-400 shrink-0">
                                      You
                                    </span>
                                  )}
                                </span>
                                {/* Percentage badge */}
                                {expenseForm.totalAmount &&
                                  participant.share &&
                                  parseFloat(expenseForm.totalAmount) > 0 && (
                                    <span className="text-[11px] font-semibold text-primary-accent dark:text-orange-400 shrink-0 bg-primary-accent/10 dark:bg-primary-accent/20 px-2 py-0.5 rounded-full border border-primary-accent/20">
                                      {(
                                        (parseFloat(participant.share) /
                                          parseFloat(expenseForm.totalAmount)) *
                                        100
                                      ).toFixed(0)}
                                      %
                                    </span>
                                  )}
                                <div className="w-28 shrink-0">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={participant.share}
                                    onChange={(e) =>
                                      handleParticipantShareChange(
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Amount"
                                    disabled={isPreviewMode}
                                    className="text-center font-semibold"
                                  />
                                </div>
                                {!isPreviewMode && (
                                  <button
                                    onClick={() =>
                                      handleRemoveParticipant(index)
                                    }
                                    className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors shrink-0"
                                    title="Remove participant"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Split bill button */}
                      {!isPreviewMode && participantInputs.length > 0 && (
                        <div className="mt-3">
                          <Button
                            onClick={handleSplitBill}
                            variant="outline"
                            className="w-full"
                            size="sm"
                          >
                            <Calculator className="w-4 h-4 mr-1" />
                            Split the Bill
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Budget Allocation Tracker */}
                {expenseForm.totalAmount &&
                  (() => {
                    const total = parseFloat(expenseForm.totalAmount) || 0;
                    const allocated = participantInputs
                      .filter((p) => p.share)
                      .reduce((sum, p) => sum + parseFloat(p.share || 0), 0);
                    const pct =
                      total > 0 ? Math.min((allocated / total) * 100, 100) : 0;
                    const isBalanced =
                      total > 0 && Math.abs(allocated - total) <= 0.01;
                    const isOver = allocated > total + 0.01;
                    return (
                      <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-gray-50 dark:bg-dark-100 p-4 space-y-2.5">
                        {/* Amount row */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600 dark:text-light-400">
                            Budget Allocation
                          </span>
                          <span
                            className={`text-sm font-semibold tabular-nums ${
                              isBalanced
                                ? "text-green-600 dark:text-green-400"
                                : isOver
                                  ? "text-red-500"
                                  : "text-gray-700 dark:text-light-200"
                            }`}
                          >
                            {allocated.toFixed(2)} / {total.toFixed(2)} EGP
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className="h-2 rounded-full bg-gray-200 dark:bg-dark-300 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              isOver
                                ? "bg-red-500"
                                : isBalanced
                                  ? "bg-green-500"
                                  : "bg-primary-accent"
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {/* Status message */}
                        {isBalanced ? (
                          <div className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            Fully allocated — ready to submit!
                          </div>
                        ) : isOver ? (
                          <p className="text-xs font-medium text-red-500">
                            Over-allocated by {(allocated - total).toFixed(2)}{" "}
                            EGP
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-light-500">
                            {(total - allocated).toFixed(2)} EGP remaining to
                            allocate
                          </p>
                        )}
                      </div>
                    );
                  })()}

                {/* Receipt Upload */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-light-200 mb-1.5 block">
                    Upload Receipt{" "}
                    <span className="text-xs font-normal text-gray-500 dark:text-light-500">
                      (Optional)
                    </span>
                  </label>
                  <input
                    ref={receiptInputRef}
                    type="file"
                    accept="image/jpg,image/jpeg,image/png,image/webp"
                    onChange={handleReceiptChange}
                    className="hidden"
                    aria-hidden
                  />
                  {receiptPreviewUrl ? (
                    <div className="inline-block">
                      <div className="relative">
                        <img
                          src={receiptPreviewUrl}
                          alt="Receipt preview"
                          className="h-32 max-w-xs rounded-xl object-cover border border-gray-200 dark:border-dark-200 shadow-sm cursor-pointer"
                          onClick={() =>
                            setReceiptImageModal(receiptPreviewUrl)
                          }
                        />
                        <button
                          type="button"
                          onClick={handleRemoveReceipt}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow"
                          aria-label="Remove receipt"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="mt-1.5 text-xs text-gray-500 dark:text-light-500 truncate max-w-xs">
                        {receiptFile?.name}
                      </p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => receiptInputRef.current?.click()}
                      disabled={isPreviewMode}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-dark-200 text-gray-500 dark:text-light-500 hover:border-primary-accent hover:text-primary-accent dark:hover:border-primary-accent dark:hover:text-orange-400 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ImageIcon className="w-5 h-5 shrink-0" />
                      <span className="text-sm">
                        Click to upload receipt image
                      </span>
                      <span className="text-xs ml-auto text-gray-400 dark:text-light-500">
                        JPG, PNG, WEBP • max 5 MB
                      </span>
                    </button>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  onClick={() => {
                    setPaymentValueTouched(true);
                    handleSubmitExpense();
                  }}
                  disabled={
                    submitting ||
                    isPreviewMode ||
                    (paymentType === "phone" &&
                      !/^01[0125]\d{8}$/.test(paymentValue.trim()))
                  }
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Create Expense
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Payment History Tab */}
          {activeTab === "history" && (
            <div className="space-y-6">
              {/* History Sub-Tabs */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-dark-100">
                <button
                  onClick={() => setHistorySubTab("made")}
                  className={`px-4 py-2 font-medium transition-colors relative ${
                    historySubTab === "made"
                      ? "text-primary-500 dark:text-primary-400"
                      : "text-light-500 dark:text-light-500 hover:text-light-100 dark:hover:text-light-100"
                  }`}
                >
                  Payments You Made
                  {historySubTab === "made" && (
                    <motion.div
                      layoutId="historyActiveTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 dark:bg-primary-400"
                    />
                  )}
                </button>
                <button
                  onClick={() => setHistorySubTab("received")}
                  className={`px-4 py-2 font-medium transition-colors relative ${
                    historySubTab === "received"
                      ? "text-primary-500 dark:text-primary-400"
                      : "text-light-500 dark:text-light-500 hover:text-light-100 dark:hover:text-light-100"
                  }`}
                >
                  Payments Received
                  {historySubTab === "received" && (
                    <motion.div
                      layoutId="historyActiveTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 dark:bg-primary-400"
                    />
                  )}
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 text-primary-500" />
                  <span className="ml-2 text-light-500">
                    Loading history...
                  </span>
                </div>
              ) : (
                (() => {
                  // Filter payments based on sub-tab
                  const filteredPayments = paymentHistory.filter((payment) => {
                    const isReceived =
                      String(payment.creditorId?._id) === String(currentUserId);
                    return historySubTab === "received"
                      ? isReceived
                      : !isReceived;
                  });

                  return filteredPayments.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white dark:bg-primary-card p-8 rounded-lg border border-gray-200 dark:border-dark-100 text-center"
                    >
                      <CheckCircle2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-light-500 dark:text-light-500">
                        {historySubTab === "made"
                          ? "No payments made yet"
                          : "No payments received yet"}
                      </p>
                    </motion.div>
                  ) : (
                    <div>
                      <h2 className="text-lg font-semibold text-light-100 dark:text-light-100 mb-4 flex items-center gap-2">
                        {historySubTab === "made" ? (
                          <>
                            <ArrowUpCircle className="w-5 h-5 text-blue-500" />
                            Payments You Made ({filteredPayments.length})
                          </>
                        ) : (
                          <>
                            <ArrowDownCircle className="w-5 h-5 text-green-500" />
                            Payments Received ({filteredPayments.length})
                          </>
                        )}
                      </h2>
                      <div className="space-y-3">
                        {filteredPayments.map((payment, index) => {
                          const isReceived =
                            String(payment.creditorId?._id) ===
                            String(currentUserId);
                          const otherPerson = isReceived
                            ? payment.debtorId
                            : payment.creditorId;

                          return (
                            <motion.div
                              key={payment._id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05,
                              }}
                              className="bg-white dark:bg-primary-card p-4 rounded-lg border border-gray-200 dark:border-dark-100"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <p className="font-semibold text-light-100 dark:text-light-100">
                                      {payment.expenseId?.description ||
                                        "Expense"}
                                    </p>
                                    <Badge
                                      variant="success"
                                      className="flex items-center gap-1"
                                    >
                                      <CheckCircle2 className="w-3 h-3" />
                                      Paid
                                    </Badge>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-sm text-light-500 dark:text-light-500 flex items-center gap-2">
                                      <User className="w-4 h-4" />
                                      {isReceived ? (
                                        <>
                                          <span className="font-medium text-green-600 dark:text-green-400">
                                            {otherPerson?.fullNameEn ||
                                              "Unknown"}
                                          </span>
                                          <span>paid you</span>
                                        </>
                                      ) : (
                                        <>
                                          <span>You paid</span>
                                          <span className="font-medium text-blue-600 dark:text-blue-400">
                                            {otherPerson?.fullNameEn ||
                                              "Unknown"}
                                          </span>
                                        </>
                                      )}
                                    </p>
                                    <p className="text-xs text-light-500 dark:text-light-500">
                                      Confirmed:{" "}
                                      {payment.confirmedAt
                                        ? new Date(
                                            payment.confirmedAt,
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "N/A"}
                                    </p>
                                    {payment.expenseId?.receiptUrl && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setReceiptImageModal(
                                            payment.expenseId.receiptUrl,
                                          )
                                        }
                                        className="mt-2 flex items-center gap-2 text-xs text-primary-accent dark:text-orange-400 hover:underline"
                                      >
                                        <img
                                          src={payment.expenseId.receiptUrl}
                                          alt="Receipt"
                                          className="w-10 h-10 rounded-md object-cover border border-gray-200 dark:border-dark-200"
                                        />
                                        <span>View Receipt</span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p
                                    className={`text-xl font-bold ${
                                      isReceived
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-blue-600 dark:text-blue-400"
                                    }`}
                                  >
                                    {payment.amount.toFixed(2)} EGP
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>
      </PageWrapper>

      {/* Receipt Image Lightbox */}
      {receiptImageModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setReceiptImageModal(null)}
        >
          <div
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setReceiptImageModal(null)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 bg-white dark:bg-dark-300 text-gray-700 dark:text-light-200 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 dark:hover:bg-dark-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={receiptImageModal}
              alt="Receipt"
              className="w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Expenses;
